import { DeviceSearchParams } from '../../hooks/useDeviceSearch';
import { DeviceSearchResponse } from '../../types';

/**
 * FS-1075 — the Devices page remembers its filters between visits.
 *
 * The page keeps its view in the URL, and the Devices menu link carries none,
 * so every visit used to start unfiltered. The facet filters are now stored
 * per account in this browser whenever the operator chooses or clears one
 * (an empty set included, so a cleared filter stays cleared) and put back
 * when the page is reached with no view in the URL. A URL that carries its
 * own view (the back arrow from a device, Open in Devices, a shared link)
 * still shows exactly what it names, and is not saved over the filters.
 *
 * Only the filters are remembered: the search text, sort, page and page size
 * start fresh on each visit.
 */

/** Facet key -> the search param it drives, plus a display label. */
export const FACET_FILTERS: { key: keyof DeviceSearchParams; facet: keyof DeviceSearchResponse['facets']; title: string }[] = [
  { key: 'entity_prefix', facet: 'entity_path', title: 'Responsibility' },
  { key: 'context_status', facet: 'context_status', title: 'Registration status' },
  { key: 'app_owner_uid', facet: 'app_owner', title: 'Owner' },
  { key: 'app_location_id', facet: 'app_location', title: 'Location' },
  { key: 'app_hvac_id', facet: 'app_hvac', title: 'HVAC' },
];

const FILTER_PARAMS: string[] = FACET_FILTERS.map((f) => f.key as string);

/** Every param that is part of the page's view (see readDeviceListView). */
const VIEW_PARAMS = [...FILTER_PARAMS, 'q', 'sort', 'dir', 'page', 'page_size'];

const SAVED_FILTERS_KEY = 'deviceIndexFilters.v1';

export const savedFiltersKey = (userId: number): string => `${SAVED_FILTERS_KEY}.${userId}`;

/**
 * The history state on every URL the page writes itself (a filter, the
 * search text, a sort, a page). A view-less URL carrying it is one the
 * operator made by clearing the last filter, so nothing is put back.
 */
export const PAGE_URL_WRITE = { deviceIndexUrlWrite: true } as const;
export const isPageUrlWrite = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && (state as { deviceIndexUrlWrite?: unknown }).deviceIndexUrlWrite === true;

/** The filters in a Devices-page URL, as {param: value}. */
export function filtersFromParams(sp: URLSearchParams): Record<string, string> {
  const filters: Record<string, string> = {};
  for (const param of FILTER_PARAMS) {
    const value = sp.get(param);
    if (value) filters[param] = value;
  }
  return filters;
}

/**
 * This account's saved filters; empty when none are saved or storage is
 * unreadable. Only the page's own filters with a non-empty string value are
 * read back.
 */
export function readSavedFilters(userId: number): Record<string, string> {
  let stored: unknown;
  try {
    stored = JSON.parse(localStorage.getItem(savedFiltersKey(userId)) ?? 'null');
  } catch {
    return {};
  }
  if (typeof stored !== 'object' || stored === null) return {};
  const filters: Record<string, string> = {};
  for (const param of FILTER_PARAMS) {
    const value = (stored as Record<string, unknown>)[param];
    if (typeof value === 'string' && value !== '') filters[param] = value;
  }
  return filters;
}

export function writeSavedFilters(userId: number, filters: Record<string, string>): void {
  try {
    localStorage.setItem(savedFiltersKey(userId), JSON.stringify(filters));
  } catch {
    /* localStorage unavailable (private mode) — remembered filters are best-effort */
  }
}

const isViewBare = (sp: URLSearchParams): boolean => VIEW_PARAMS.every((param) => !sp.has(param));

const withFilters = (sp: URLSearchParams, filters: Record<string, string>): URLSearchParams => {
  const next = new URLSearchParams(sp);
  for (const [param, value] of Object.entries(filters)) next.set(param, value);
  return next;
};

/**
 * The URL the page should show instead of `sp`, or null when `sp` stands.
 *
 *  - Reached with no view in the URL, not by the page's own write: this
 *    account's saved filters, when it has any.
 *  - `accountSwitched`: the account changed while the page was open, so the
 *    view on screen is the previous account's. It is cleared and this
 *    account's saved filters put in its place.
 */
export function restoredView(
  sp: URLSearchParams,
  saved: Record<string, string>,
  { pageWrote, accountSwitched }: { pageWrote: boolean; accountSwitched: boolean },
): URLSearchParams | null {
  if (accountSwitched) {
    const cleared = new URLSearchParams(sp);
    for (const param of VIEW_PARAMS) cleared.delete(param);
    const next = withFilters(cleared, saved);
    return next.toString() === sp.toString() ? null : next;
  }
  if (pageWrote || !isViewBare(sp) || Object.keys(saved).length === 0) return null;
  return withFilters(sp, saved);
}
